import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Database,
  FileCheck2,
  FileText,
  Fingerprint,
  Layers3,
  LockKeyhole,
  Network,
  Handshake,
  ShieldCheck,
  Table2,
} from "lucide-react";
import Logo from "@/components/Logo";

const navItems = [
  { label: "Workflow", href: "#workflow" },
  { label: "Use cases", href: "#use-cases" },
  { label: "Trust", href: "#trust" },
  { label: "Early access", href: "#early-access" },
];

const financeTeams = [
  "Banks",
  "NBFCs",
  "Private credit / Venture debt",
  "PE / VC",
  "Family offices / MFs",
];

const focusDeskTeams = [
  "Credit & underwriting",
  "Portfolio / fund ops",
  "Research & IC",
  "Capital markets / transactions",
  "Family office reporting",
];

const docInfraExamples = [
  "Auto-draft board packs from MIS and filings",
  "IC memos and LP updates in your fund templates",
  "Credit committee papers with covenant traceability",
  "Transaction and investor documentation from data room and models",
];

const workflow = [
  {
    icon: Database,
    title: "Ingest",
    body: "One controlled finance workspace. PDFs, filings, Excel models, CIMs, transcripts, MIS packs, and internal templates — connected, not scattered.",
  },
  {
    icon: Layers3,
    title: "Structure",
    body: "Every figure, assumption, covenant, and KPI becomes a traceable data point — pulled from its source.",
  },
  {
    icon: FileText,
    title: "Generate",
    body: "Draft investment memos, credit papers, research notes, portfolio reviews, and transaction documents in the format your desk already uses — not a generic output you have to fix.",
  },
  {
    icon: FileCheck2,
    title: "Review",
    body: "Every output is review-ready — source links intact, metrics structured, gaps flagged, and edit history preserved so nothing leaves the desk unverified.",
  },
];

const coreThemes = [
  {
    title: "Financial data context",
    body: "Every number has a definition and source. Cosalpha keeps it connected — across financial models, filings, board packs, KPI trackers, covenant reports, and company documents.",
  },
  {
    title: "Finance-domain documentation",
    body: "Built for research, credit, private markets, family office, and capital markets — without reducing any desk to a generic workflow.",
  },
];

const useCases = [
  {
    title: "Private Markets",
    body: "Portfolio companies don't send data the same way. Cosalpha structures MIS packs, board decks, and operating KPIs into IC memos, LP updates, and portfolio reviews — in your fund's format, every cycle.",
    tags: ["Operating KPIs", "MIS packs", "Fund reporting"],
  },
  {
    title: "Credit Analysis",
    body: "Connect borrower MIS, covenant reporting, cash-flow assumptions, and downside cases into committee-ready credit documentation.",
    tags: ["Covenants", "Borrower MIS", "Risk memos"],
  },
  {
    title: "Capital Markets",
    body: "Connect data room material, financial models, diligence trackers, and investor materials into source-backed transaction documentation.",
    tags: ["Datarooms", "Models", "Transaction docs"],
  },
];

const trustLayer = [
  {
    icon: Fingerprint,
    title: "Analyst-in-the-loop review",
    body: "Analysts approve, edit, reject, and annotate before anything leaves the desk.",
  },
  {
    icon: Table2,
    title: "Structured financial metrics",
    body: "Revenue, margins, leverage, covenants, and operating KPIs treated as structured data.",
  },
  {
    icon: Network,
    title: "Source traceability",
    body: "Each output keeps a defensible path from financial data and source material to generated section.",
  },
  {
    icon: ShieldCheck,
    title: "Data protection and security",
    body: "Designed for controlled deployments with access controls, permissioning, private environments, and clear data-handling boundaries.",
  },
  {
    icon: LockKeyhole,
    title: "Private deployment",
    body: "Deployment can be configured for private cloud, VPC, or controlled enterprise environments.",
  },
  {
    icon: Handshake,
    title: "Hands-on implementation",
    body: "We work closely with finance teams on data audits, workflow mapping, template calibration, onboarding, and effective implementation timelines.",
  },
];

const productOutputBullets = [
  "Desk-specific formatting and section order",
  "Source links intact through every draft",
  "Every figure traceable to its source document",
];

const PRODUCT_ASSETS = {
  useCases: "/product/use-cases.png",
  productOutput: "/product/product-output.png",
};

function SmallCaps({ children, className = "" }) {
  return <div className={`smallcaps ${className}`}>{children}</div>;
}

function SectionHeading({ label, title, body, dark = false, compact = false }) {
  return (
    <div className={`max-w-[760px] ${compact ? "shrink-0" : ""}`}>
      <SmallCaps className="text-[var(--saffron)]">{label}</SmallCaps>
      <h2
        className={`font-serif font-medium leading-[1.05] tracking-[-0.015em] ${compact ? "mt-2 text-[28px] sm:text-[34px]" : "mt-5 text-[34px] sm:text-[46px]"} ${dark ? "text-white" : "text-[var(--ink)]"}`}
      >
        {title}
      </h2>
      {body && (
        <p
          className={`${compact ? "mt-3 text-[13px] leading-[1.6] lg:line-clamp-3" : "mt-5 text-[15px] leading-[1.75]"} ${dark ? "text-white/68" : "text-[var(--warm-grey)]"}`}
        >
          {body}
        </p>
      )}
    </div>
  );
}

function ButtonLink({ to, href, children, variant = "primary", testId }) {
  const classes =
    variant === "primary"
      ? "marketing-cta-primary inline-flex items-center gap-2 border px-6 py-3 text-[14px] font-semibold shadow-none transition-colors"
      : "inline-flex items-center gap-2 border border-white/25 bg-transparent px-6 py-3 text-[14px] font-semibold text-white transition-colors hover:border-white/45 hover:bg-white/10";

  if (to) {
    return (
      <Link to={to} className={classes} data-testid={testId}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} className={classes} data-testid={testId}>
      {children}
    </a>
  );
}

function HeroContextGraphPanel() {
  return (
    <div className="w-full border border-white/12 bg-white/[0.035] p-5 shadow-[0_24px_80px_rgba(0,0,0,.32)] backdrop-blur">
      <div className="flex items-center justify-between border-b border-white/10 pb-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/48">Finance context graph</span>
        <span className="h-2 w-2 bg-[var(--saffron)]" />
      </div>
      <div className="mt-5 space-y-3">
        {["Financial data", "Source documents", "Desk format", "Reviewer queue"].map((item, index) => (
          <div key={item} className="grid grid-cols-[42px_1fr_auto] items-center gap-3 border border-white/10 bg-[var(--night)]/70 p-3">
            <span className="font-mono text-[11px] text-[var(--saffron)]">{String(index + 1).padStart(2, "0")}</span>
            <span className="text-[13px] text-white/76">{item}</span>
            <Check className="h-4 w-4 text-[var(--saffron)]" strokeWidth={2.2} />
          </div>
        ))}
      </div>
      <div className="mt-5 grid grid-cols-2 gap-px bg-white/10">
        {financeTeams.map((team) => (
          <div key={team} className="bg-[var(--night)] p-3 text-[11px] leading-snug text-white/62">
            {team}
          </div>
        ))}
      </div>
      <div className="mt-5 border-t border-white/10 pt-5">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/48">What you can generate</span>
        <ul className="mt-3 space-y-2">
          {docInfraExamples.map((example) => (
            <li key={example} className="flex gap-2 text-[12px] leading-[1.5] text-white/68">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--saffron)]" strokeWidth={2.2} />
              <span>{example}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function FooterLink({ label, href, to }) {
  const className = "text-white/62 transition-colors hover:text-white";
  if (to) {
    return (
      <Link to={to} className={className}>
        {label}
      </Link>
    );
  }
  return (
    <a href={href} className={className}>
      {label}
    </a>
  );
}

function ProductVisual({ src, alt, fallback: Fallback, className = "", frameLabel }) {
  const [useFallback, setUseFallback] = useState(false);

  return (
    <figure className={`relative flex h-full overflow-hidden border border-[var(--border)] bg-[var(--cream)] ${className}`} aria-label={alt}>
      {frameLabel && (
        <figcaption className="sr-only">{frameLabel}</figcaption>
      )}
      {useFallback ? (
        <div className="flex h-full min-h-0 w-full flex-1 items-stretch">
          <Fallback />
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          className="h-full min-h-0 w-full flex-1 object-contain object-center"
          onError={() => setUseFallback(true)}
        />
      )}
    </figure>
  );
}

const illustration = {
  cream: "#F5F5F0",
  panel: "#111821",
  panel2: "#151F2A",
  night: "#0B0F14",
  line: "#2B3543",
  softLine: "#202A36",
  text: "#F8FAFC",
  muted: "#6B7280",
  mutedLight: "#9CA8BA",
  accent: "#FF8A3D",
  accentSoft: "#3A2419",
  blue: "#4B7BFF",
  accentMuted: "#3A2419",
};

const financialSources = [
  { name: "FY24 Annual Report.pdf", meta: "p.190 · source linked", type: "pdf" },
  { name: "Q2 Financials.xlsx", meta: "model tab · metric checked", type: "sheet" },
  { name: "Credit Note.docx", meta: "committee draft", type: "note" },
  { name: "Investor Update.pdf", meta: "source linked", type: "pdf" },
];

const financialFacts = [
  { ref: "1", metric: "Revenue", value: "₹12,480 Cr", status: "source linked", kind: "rupee" },
  { ref: "2", metric: "EBITDA Margin", value: "18.4%", status: "metric checked", kind: "percent" },
  { ref: "3", metric: "Net Debt / EBITDA", value: "2.1x", status: "review", kind: "multiple" },
  { ref: "4", metric: "Working Capital", value: "+320 bps", status: "metric checked", kind: "bps" },
];

const memoLines = [
  { text: "Revenue +18% YoY", ref: "1" },
  { text: "Margin 18.4%", ref: "2" },
  { text: "Leverage 2.1x", ref: "3" },
  { text: "WC +320 bps", ref: "4" },
];

const reviewerRoles = ["Analyst", "VP", "Partner"];

const segmentContextCards = [
  {
    title: "Sector Drivers",
    type: "sector",
    rows: ["Credit growth", "Yield pressure", "Asset quality"],
    status: "sector mapped",
  },
  {
    title: "Peer Set",
    type: "peer",
    rows: ["NBFC peer group", "Leverage band", "Margin range"],
    status: "peer linked",
  },
  {
    title: "Risk Signals",
    type: "risk",
    rows: ["Covenant headroom", "ALM mismatch", "Borrower concentration"],
    status: "risk reviewed",
  },
  {
    title: "Capital Context",
    type: "capital",
    rows: ["Banks", "Private credit / Venture debt", "AIF / family office"],
    status: "capital context",
  },
];

const segmentMatrixCells = [
  "Banks",
  "NBFCs",
  "Private credit / Venture debt",
  "PE / VC",
  "Family offices / MFs",
];

const sectorRows = [
  { name: "Banks", growth: 74, margin: 62, risk: 36, marketShare: 71, signal: "Capital inflow stable" },
  { name: "NBFCs", growth: 69, margin: 58, risk: 44, marketShare: 63, signal: "Credit spread tightening" },
  { name: "Private credit / Venture debt", growth: 64, margin: 54, risk: 52, marketShare: 57, signal: "Covenant watch active" },
  { name: "PE / VC", growth: 72, margin: 49, risk: 47, marketShare: 67, signal: "Valuation dispersion" },
  { name: "Family offices / MFs", growth: 58, margin: 57, risk: 33, marketShare: 49, signal: "Defensive rotation" },
];

const activityBadges = [
  { label: "Reviewing", icon: "review", color: illustration.accent },
  { label: "Flagging", icon: "flag", color: illustration.blue },
  { label: "Approval", icon: "check", color: illustration.accent },
  { label: "Real time", icon: "pulse", color: illustration.accent },
];

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);
    return () => mediaQuery.removeEventListener("change", updatePreference);
  }, []);

  return prefersReducedMotion;
}

function useCyclingIndex(length, intervalMs) {
  const [activeIndex, setActiveIndex] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % length);
    }, intervalMs);

    return () => window.clearInterval(timer);
  }, [length, intervalMs, prefersReducedMotion]);

  return activeIndex;
}

function SvgTextLine({ x, y, w, h = 8, fill = illustration.line }) {
  return <rect x={x} y={y} width={w} height={h} rx="2" fill={fill} />;
}

function SourceWorkflowGlyph({ type, active = false }) {
  const stroke = active ? "#FF8A3D" : "rgba(255,255,255,0.34)";
  const muted = active ? "rgba(255,138,61,0.5)" : "rgba(255,255,255,0.18)";

  if (type === "sheet") {
    return (
      <svg viewBox="0 0 28 28" className="h-4 w-4" aria-hidden="true">
        <rect x="4" y="5" width="20" height="18" fill="none" stroke={stroke} strokeWidth="1.3" />
        <path d="M 4 10 H 24 M 4 15 H 24 M 4 20 H 24 M 11 5 V 23 M 18 5 V 23" stroke={muted} strokeWidth="1" />
        <rect x="5.5" y="15.8" width="17" height="3.2" fill={active ? "rgba(255,138,61,0.22)" : "rgba(255,255,255,0.08)"} />
      </svg>
    );
  }

  if (type === "note") {
    return (
      <svg viewBox="0 0 28 28" className="h-4 w-4" aria-hidden="true">
        <path d="M 7 4 H 18 L 22 8 V 24 H 7 Z" fill="none" stroke={stroke} strokeWidth="1.3" />
        <path d="M 18 4 V 8 H 22" fill="none" stroke={stroke} strokeWidth="1.3" />
        <path d="M 10 12 H 19 M 10 16 H 18 M 10 20 H 15" stroke={muted} strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 28 28" className="h-4 w-4" aria-hidden="true">
      <path d="M 7 4 H 18 L 22 8 V 24 H 7 Z" fill="none" stroke={stroke} strokeWidth="1.3" />
      <path d="M 18 4 V 8 H 22" fill="none" stroke={stroke} strokeWidth="1.3" />
      <path d="M 10 13 H 19 M 10 17 H 17" stroke={muted} strokeWidth="1.2" strokeLinecap="round" />
      <text x="10" y="22" fill={active ? "#FF8A3D" : "#8A8F98"} fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fontSize="5.4" fontWeight="700">
        PDF
      </text>
    </svg>
  );
}

function MetricGlyph({ kind, active = false }) {
  const symbol = { rupee: "₹", percent: "%", multiple: "x", bps: "bp" }[kind] || "₹";

  return (
    <div className={`grid h-7 w-7 place-items-center border transition-colors duration-700 ${active ? "border-[#FF8A3D]/70 bg-[#2A1810] text-[#FF8A3D]" : "border-white/12 bg-[#0A0B0D] text-[#8A8F98]"}`}>
      <div className="flex w-4 flex-col gap-[2px]">
        <span className="font-mono text-[8px] leading-none">{symbol}</span>
        <span className={`h-px ${active ? "bg-[#FF8A3D]/80" : "bg-white/20"}`} />
        <span className={`h-px w-3 ${active ? "bg-[#FF8A3D]/70" : "bg-white/16"}`} />
      </div>
    </div>
  );
}

function EvidenceMarker({ refId, active = false, compact = false }) {
  return (
    <div className={`inline-flex items-center gap-1.5 font-mono uppercase tracking-[0.1em] ${compact ? "text-[7px]" : "text-[8px]"} ${active ? "text-[#FF8A3D]" : "text-[#8A8F98]"}`}>
      <span>{compact ? `[${refId}]` : `reference [${refId}]`}</span>
      <svg viewBox="0 0 18 8" className="h-2.5 w-5" aria-hidden="true">
        <circle cx="4" cy="4" r="2.2" fill="none" stroke={active ? "#FF8A3D" : "rgba(255,255,255,0.28)"} strokeWidth="1.2" />
        <circle cx="14" cy="4" r="2.2" fill="none" stroke={active ? "#FF8A3D" : "rgba(255,255,255,0.28)"} strokeWidth="1.2" />
        <path d="M 6.2 4 H 11.8" stroke={active ? "#FF8A3D" : "rgba(255,255,255,0.22)"} strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    </div>
  );
}

function MemoDocumentGlyph({ active = false }) {
  return (
    <svg viewBox="0 0 38 44" className="h-8 w-7" aria-hidden="true">
      <path d="M 7 3 H 25 L 32 10 V 40 H 7 Z" fill="#0D0F12" stroke={active ? "#FF8A3D" : "rgba(255,255,255,0.22)"} strokeWidth="1.3" />
      <path d="M 25 3 V 10 H 32" fill="none" stroke={active ? "#FF8A3D" : "rgba(255,255,255,0.22)"} strokeWidth="1.3" />
      <path d="M 12 16 H 27 M 12 22 H 22 M 12 28 H 26" stroke={active ? "rgba(255,138,61,0.55)" : "rgba(255,255,255,0.18)"} strokeWidth="1.3" strokeLinecap="round" />
      <text x="12" y="36" fill={active ? "#FF8A3D" : "#8A8F98"} fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fontSize="5.5">
        [1]
      </text>
    </svg>
  );
}

function ContextGlyph({ type, active = false }) {
  const stroke = active ? "#FF8A3D" : "rgba(255,255,255,0.34)";
  const fill = active ? "#FF8A3D" : "rgba(255,255,255,0.24)";

  if (type === "sector") {
    return (
      <svg viewBox="0 0 30 30" className="h-4 w-4" aria-hidden="true">
        <path d="M 5 22 C 10 19, 13 15, 17 14 C 21 13, 22 9, 25 7" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" />
        {[5, 17, 25].map((x, index) => (
          <circle key={x} cx={x} cy={[22, 14, 7][index]} r="2" fill={fill} />
        ))}
      </svg>
    );
  }

  if (type === "peer") {
    return (
      <svg viewBox="0 0 30 30" className="h-4 w-4" aria-hidden="true">
        <path d="M 9 10 H 15 M 15 10 L 21 17 M 9 20 H 21" stroke={active ? "#FF8A3D" : "rgba(255,255,255,0.22)"} strokeWidth="1.2" />
        <rect x="3" y="6" width="8" height="8" fill="none" stroke={stroke} strokeWidth="1.3" />
        <rect x="19" y="13" width="8" height="8" fill="none" stroke={stroke} strokeWidth="1.3" />
        <rect x="3" y="16" width="8" height="8" fill="none" stroke={stroke} strokeWidth="1.3" />
      </svg>
    );
  }

  if (type === "risk") {
    return (
      <svg viewBox="0 0 30 30" className="h-4 w-4" aria-hidden="true">
        <path d="M 15 5 L 26 24 H 4 Z" fill="none" stroke={stroke} strokeWidth="1.4" strokeLinejoin="round" />
        <path d="M 15 12 V 17" stroke={fill} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="15" cy="21" r="1.2" fill={fill} />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 30 30" className="h-4 w-4" aria-hidden="true">
      <path d="M 5 12 L 15 6 L 25 12" fill="none" stroke={stroke} strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M 7 13 H 23 M 8 23 H 22 M 10 13 V 23 M 15 13 V 23 M 20 13 V 23" stroke={stroke} strokeWidth="1.3" strokeLinecap="round" />
      <rect x="4" y="23" width="22" height="2.5" fill={fill} opacity="0.75" />
    </svg>
  );
}

function SegmentMatrixGlyph({ active = false }) {
  return (
    <svg viewBox="0 0 30 22" className="h-4 w-5" aria-hidden="true">
      <rect x="1" y="1" width="28" height="20" fill="none" stroke={active ? "#FF8A3D" : "rgba(255,255,255,0.28)"} strokeWidth="1" />
      <path d="M 15 1 V 21 M 1 8 H 29 M 1 15 H 29" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
      <rect x="16" y="9" width="12" height="5" fill={active ? "rgba(255,138,61,0.24)" : "rgba(255,255,255,0.08)"} />
    </svg>
  );
}

function FinancialAnalysisVisual() {
  const activeFactIndex = useCyclingIndex(financialFacts.length, 3200);
  const activeIndex = activeFactIndex % financialSources.length;

  return (
    <div className="relative flex h-full min-h-0 w-full overflow-hidden border border-white/16 bg-[#0A0B0D] p-3 text-[#F5F5F0] shadow-[0_24px_70px_rgba(0,0,0,0.28)] sm:p-4">
      <div className="pointer-events-none absolute inset-0 opacity-[0.12] [background-image:linear-gradient(rgba(255,255,255,.18)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.18)_1px,transparent_1px)] [background-size:48px_48px]" />
      <div className="relative z-10 flex min-h-0 w-full flex-col">
        <div className="flex items-start justify-between border-b border-white/12 pb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#F5F5F0]">Financial Analysis Engine</div>
            <div className="mt-2 font-mono text-[8px] uppercase tracking-[0.18em] text-[#8A8F98]">Numbers linked to source</div>
          </div>
          <div className="h-2.5 w-2.5 bg-[#FF8A3D] motion-safe:animate-pulse" />
        </div>

        <div className="relative grid flex-1 min-h-0 grid-cols-[0.82fr_1.28fr_1fr] gap-2 pt-3 sm:gap-3 sm:pt-4">
          <svg className="pointer-events-none absolute inset-0 z-0 hidden h-full w-full md:block" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {[
              "M 24 19 C 34 19, 36 28, 44 28",
              "M 24 37 C 34 37, 36 44, 44 44",
              "M 24 53 C 34 53, 36 60, 44 60",
              "M 24 69 C 34 69, 36 76, 44 76",
            ].map((path, index) => (
              <path
                key={path}
                d={path}
                fill="none"
                stroke={index === activeIndex ? "#FF8A3D" : "rgba(255,255,255,0.12)"}
                strokeWidth={index === activeIndex ? "0.34" : "0.18"}
                opacity={index === activeIndex ? 0.9 : 0.2}
                className="transition-all duration-700 ease-in-out"
              />
            ))}
            {[
              "M 68 28 C 74 28, 76 30, 82 30",
              "M 68 44 C 74 44, 76 44, 82 44",
              "M 68 60 C 74 60, 76 58, 82 58",
              "M 68 76 C 74 76, 76 70, 82 70",
            ].map((path, index) => (
              <path
                key={path}
                d={path}
                fill="none"
                stroke={index === activeFactIndex ? "#FF8A3D" : "rgba(255,255,255,0.1)"}
                strokeWidth={index === activeFactIndex ? "0.28" : "0.16"}
                opacity={index === activeFactIndex ? 0.76 : 0.18}
                className="transition-all duration-700 ease-in-out"
              />
            ))}
          </svg>

          <div className="relative z-10 min-h-0 border border-white/12 bg-[#111418]/92 p-2.5">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#8A8F98]">Source stack</span>
              <span className="font-mono text-[8px] uppercase tracking-[0.16em] text-[#FF8A3D]">controlled docs</span>
            </div>
            <div className="space-y-2">
              {financialSources.map(({ name, meta, type }, index) => {
                const isActive = index === activeIndex;
                return (
                  <div
                    key={name}
                    className={`relative border bg-[#0D0F12] p-2 transition-all duration-700 ease-in-out ${
                      isActive ? "translate-x-1 border-[#FF8A3D]/70 shadow-[0_0_0_1px_rgba(255,138,61,0.16)]" : "border-white/12"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`flex h-7 w-7 items-center justify-center border ${isActive ? "border-[#FF8A3D]/55 text-[#FF8A3D]" : "border-white/12 text-[#8A8F98]"}`}>
                        <SourceWorkflowGlyph type={type} active={isActive} />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-[10.5px] text-[#F5F5F0]">{name}</div>
                        <div className="mt-1 font-mono text-[8px] uppercase tracking-[0.12em] text-[#8A8F98]">{meta}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="relative z-10 min-h-0 border border-white/12 bg-[#111418]/92 p-2.5">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#8A8F98]">Metric evidence layer</span>
              <EvidenceMarker refId={financialFacts[activeFactIndex].ref} active />
            </div>
            <div className="space-y-2">
              {financialFacts.map(({ ref, metric, value, status, kind }, index) => {
                const isActive = index === activeFactIndex;
                return (
                  <div
                    key={metric}
                    className={`relative border p-2.5 transition-all duration-700 ease-in-out ${
                      isActive ? "translate-y-[-2px] border-[#FF8A3D]/70 bg-[#1A1713]" : "border-white/12 bg-[#0D0F12]"
                    }`}
                  >
                    {isActive && <div className="absolute inset-x-3 top-0 h-px bg-[#FF8A3D]/80" />}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <MetricGlyph kind={kind} active={isActive} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-[11px] text-[#F5F5F0]">{metric}</span>
                            <EvidenceMarker refId={ref} active={isActive} compact />
                          </div>
                          <div className="mt-1 font-mono text-[13px] text-[#F5F5F0]">{value}</div>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className={`border px-2 py-1 font-mono text-[8px] uppercase tracking-[0.12em] ${isActive ? "border-[#FF8A3D]/60 text-[#FF8A3D]" : "border-white/12 text-[#8A8F98]"}`}>
                          {status}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="relative z-10 min-h-0 border border-white/12 bg-[#111418]/92 p-2">
            <div className="mb-2 flex items-center justify-between border-b border-white/10 pb-2">
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#8A8F98]">Memo output</div>
                <div className="mt-1 text-[13px] leading-tight text-[#F5F5F0]">Credit Memo</div>
                <div className="mt-1 font-mono text-[7px] uppercase tracking-[0.14em] text-[#FF8A3D]">source-linked narrative</div>
              </div>
              <MemoDocumentGlyph active />
            </div>
            <div className="space-y-1.5">
              {memoLines.map(({ text, ref }, index) => {
                const isActive = index === activeFactIndex;
                return (
                  <div key={text} className={`border-l px-2 py-1.5 text-[9.5px] leading-tight transition-all duration-700 ease-in-out ${isActive ? "border-[#FF8A3D] bg-[#1A1713] text-[#F5F5F0]" : "border-white/12 text-[#8A8F98]"}`}>
                    {text}
                    <span className={`ml-1 font-mono ${isActive ? "text-[#FF8A3D]" : "text-[#8A8F98]"}`}>[{ref}]</span>
                    {isActive && <span className="mt-1 block font-mono text-[7px] uppercase tracking-[0.12em] text-[#FF8A3D]">memo-ready</span>}
                  </div>
                );
              })}
            </div>
            <div className="mt-2 border-t border-white/10 pt-2">
              <div className="mb-1.5 font-mono text-[8px] uppercase tracking-[0.18em] text-[#8A8F98]">Reviewer queue</div>
              <div className="space-y-1">
                {reviewerRoles.map((role, index) => {
                  const isActive = index <= activeFactIndex;
                  return (
                    <div key={role} className="flex items-center justify-between border border-white/12 bg-[#0D0F12] px-2 py-1">
                      <span className="text-[8.5px] text-[#F5F5F0]">{role}</span>
                      <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border ${isActive ? "border-[#FF8A3D] text-[#FF8A3D]" : "border-white/14 text-[#8A8F98]"}`}>
                        {isActive ? <Check className="h-2.5 w-2.5 motion-safe:animate-pulse" strokeWidth={2.2} /> : null}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SegmentAnalysisVisual() {
  const activeIndex = useCyclingIndex(segmentContextCards.length, 3200);
  const activeMatrixIndex = useCyclingIndex(segmentMatrixCells.length, 4400);
  const activeStatus = segmentContextCards[activeIndex].status;
  const cardPositions = [
    "left-[4%] top-[18%]",
    "right-[4%] top-[18%]",
    "left-[4%] top-[48%]",
    "right-[4%] top-[48%]",
  ];
  const linePaths = [
    "M 49 43 C 39 37, 31 31, 23 28",
    "M 51 43 C 62 37, 69 31, 77 28",
    "M 49 51 C 39 57, 31 61, 23 60",
    "M 51 51 C 62 57, 69 61, 77 60",
  ];

  return (
    <div className="relative flex h-full w-full overflow-hidden border border-white/16 bg-[#0A0B0D] p-4 text-[#F5F5F0] shadow-[0_24px_70px_rgba(0,0,0,0.28)]">
      <div className="pointer-events-none absolute inset-0 opacity-[0.12] [background-image:linear-gradient(rgba(255,255,255,.18)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.18)_1px,transparent_1px)] [background-size:48px_48px]" />
      <div className="relative z-10 flex min-h-0 w-full flex-col">
        <div className="flex items-start justify-between border-b border-white/12 pb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#F5F5F0]">Sector Context Graph</div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[8px] uppercase tracking-[0.16em] text-[#8A8F98]">
              <span>Context ID: CX-204</span>
              <span>Updated: Live</span>
              <span>Sources: 18</span>
            </div>
          </div>
          <div className="h-2.5 w-2.5 bg-[#FF8A3D] motion-safe:animate-pulse" />
        </div>

        <div className="relative flex-1 min-h-0 pt-4">
          <svg className="pointer-events-none absolute inset-0 z-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {linePaths.map((path, index) => (
              <path
                key={path}
                d={path}
                fill="none"
                stroke={index === activeIndex ? "#FF8A3D" : "rgba(255,255,255,0.12)"}
                strokeWidth={index === activeIndex ? "0.34" : "0.16"}
                opacity={index === activeIndex ? 0.9 : 0.22}
                className="transition-all duration-700 ease-in-out"
              />
            ))}
          </svg>

          <div className="absolute left-1/2 top-[26%] z-20 w-[34%] -translate-x-1/2 border border-[#FF8A3D]/55 bg-[#111418]/95 p-3 shadow-[0_0_0_1px_rgba(255,138,61,0.12)] transition-transform duration-700 ease-in-out motion-safe:translate-y-[-2px]">
            <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-3">
              <div>
                <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-[#8A8F98]">Company Context</div>
                <div className="mt-1 text-[15px] text-[#F5F5F0]">SampleCo</div>
              </div>
              <div className="flex h-8 w-8 items-center justify-center border border-[#FF8A3D]/50 text-[#FF8A3D]">
                <ContextGlyph type="capital" active />
              </div>
            </div>
            <div className="grid gap-2 font-mono text-[8px] uppercase tracking-[0.12em] text-[#8A8F98]">
              {[
                ["Issuer", "Evercredit FinServ"],
                ["Sector", "NBFC / Financial Services"],
                ["Stage", "Performing credit"],
                ["Coverage", "IC note / sector update"],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between gap-3 border border-white/10 bg-[#0D0F12] px-2 py-1.5">
                  <span>{label}</span>
                  <span className="truncate text-right text-[#F5F5F0]">{value}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 inline-flex items-center gap-2 border border-[#FF8A3D]/50 bg-[#1A1713] px-2 py-1 font-mono text-[8px] uppercase tracking-[0.14em] text-[#FF8A3D]">
              <span className="h-1.5 w-1.5 bg-[#FF8A3D]" />
              {activeStatus}
            </div>
          </div>

          {segmentContextCards.map(({ title, type, rows, status }, index) => {
            const isActive = index === activeIndex;
            return (
              <div
                key={title}
                className={`absolute z-10 w-[29%] border bg-[#111418]/94 p-2.5 transition-all duration-700 ease-in-out ${cardPositions[index]} ${
                  isActive ? "translate-y-[-4px] border-[#FF8A3D]/65 shadow-[0_0_0_1px_rgba(255,138,61,0.14)]" : "border-white/16"
                }`}
              >
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`flex h-6 w-6 items-center justify-center border ${isActive ? "border-[#FF8A3D]/55 text-[#FF8A3D]" : "border-white/12 text-[#8A8F98]"}`}>
                      <ContextGlyph type={type} active={isActive} />
                    </div>
                    <div className="text-[11px] text-[#F5F5F0]">{title}</div>
                  </div>
                  {isActive && <Check className="h-3.5 w-3.5 text-[#FF8A3D] motion-safe:animate-pulse" strokeWidth={2} />}
                </div>
                <div className="mb-2 font-mono text-[7px] uppercase tracking-[0.14em] text-[#8A8F98]">
                  {index === 0 ? "company to sector" : index === 1 ? "peer evidence" : index === 2 ? "review flags" : "audience routing"}
                </div>
                <div className="space-y-1">
                  {rows.map((row) => (
                    <div key={row} className="flex items-center gap-2 font-mono text-[7.5px] uppercase tracking-[0.1em] text-[#8A8F98]">
                      <span className={`h-1.5 w-1.5 ${isActive ? "bg-[#FF8A3D]" : "bg-white/18"}`} />
                      <span>{row}</span>
                    </div>
                  ))}
                </div>
                <div className={`mt-2 inline-flex border px-2 py-1 font-mono text-[7px] uppercase tracking-[0.13em] ${isActive ? "border-[#FF8A3D]/55 text-[#FF8A3D]" : "border-white/10 text-[#8A8F98]"}`}>
                  {status}
                </div>
              </div>
            );
          })}

          <div className="absolute inset-x-[4%] bottom-0 z-20 border border-white/16 bg-[#0D0F12]/96 p-2.5">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#8A8F98]">Segment routing</span>
              <span className="inline-flex items-center gap-1 font-mono text-[8px] uppercase tracking-[0.14em] text-[#FF8A3D]">
                <SegmentMatrixGlyph active />
                segment routed
              </span>
            </div>
            <div className="grid grid-cols-2 border border-white/10">
              {segmentMatrixCells.map((cell, index) => {
                const isActive = index === activeMatrixIndex;
                return (
                  <div
                    key={cell}
                    className={`border border-white/10 px-3 py-2 transition-all duration-700 ease-in-out ${
                      isActive ? "bg-[#1C2917] text-[#F5F5F0] shadow-[inset_0_0_0_1px_rgba(255,138,61,0.45)]" : "bg-[#0A0B0D] text-[#F5F5F0]"
                    }`}
                  >
                    <div className="text-[11px]">{cell}</div>
                    <div className={`mt-1 font-mono text-[7px] uppercase tracking-[0.12em] ${isActive ? "text-[#FF8A3D]" : "text-[#8A8F98]"}`}>audience node</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SvgActivityIcon({ icon, color }) {
  if (icon === "flag") {
    return (
      <g>
        <path d="M 0 0 L 0 20" stroke={color} strokeWidth="2" strokeLinecap="round" />
        <path d="M 2 2 L 17 5 L 2 10 Z" fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
      </g>
    );
  }

  if (icon === "check") {
    return <path d="M 1 11 L 7 17 L 19 4" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />;
  }

  if (icon === "pulse") {
    return <path d="M 0 11 L 5 11 L 8 4 L 13 18 L 17 11 L 22 11" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />;
  }

  return (
    <g>
      <circle cx="10" cy="10" r="8" fill="none" stroke={color} strokeWidth="2" />
      <circle cx="10" cy="10" r="2.5" fill={color} />
    </g>
  );
}

function SvgActivityBadges({ x, y, activeIndex, compact = false }) {
  const c = illustration;
  const badgeWidth = compact ? 112 : 128;
  const badgeHeight = compact ? 30 : 32;
  const gap = compact ? 8 : 10;
  const iconScale = compact ? 0.72 : 0.78;

  return (
    <g transform={`translate(${x} ${y})`}>
      {activityBadges.map((badge, index) => {
        const isActive = index === activeIndex;
        return (
          <g key={badge.label} transform={`translate(${index * (badgeWidth + gap)} 0)`} opacity={isActive ? 1 : 0.36}>
            <rect
              width={badgeWidth}
              height={badgeHeight}
              rx={badgeHeight / 2}
              fill={isActive ? "#111820" : "#0B1118"}
              stroke={isActive ? badge.color : c.line}
              strokeOpacity={isActive ? 0.95 : 0.8}
            />
            <g transform={`translate(${compact ? 13 : 14} ${compact ? 7 : 7}) scale(${iconScale})`}>
              <SvgActivityIcon icon={badge.icon} color={badge.color} />
            </g>
            <text x={compact ? "36" : "40"} y={compact ? "20" : "21"} fill={c.text} fontFamily="Inter, system-ui, sans-serif" fontSize={compact ? "9.5" : "10"} fontWeight="400">
              {badge.label}
            </text>
            {isActive && (
              <animate attributeName="opacity" values="0;1;1;0.45;1" dur="3s" repeatCount="indefinite" />
            )}
          </g>
        );
      })}
    </g>
  );
}

function CosalphaSvgLogo({ x, y, inverse = false }) {
  const c = illustration;

  return (
    <g transform={`translate(${x} ${y})`}>
      <text x="0" y="0" fill={inverse ? c.text : c.night} fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="700" letterSpacing="1.4">
        COSALPHA
      </text>
    </g>
  );
}

function StatusChip({ x, y, label, color = illustration.accent, w = 82 }) {
  const c = illustration;

  return (
    <g transform={`translate(${x} ${y})`}>
      <rect width={w} height="24" rx="6" fill={color === c.accent ? c.accentSoft : "transparent"} stroke={color} strokeWidth="1.5" />
      <text x={w / 2} y="16" textAnchor="middle" fill={color} fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800">
        {label}
      </text>
    </g>
  );
}

function WorkspaceScreenSvg() {
  const c = illustration;

  return (
    <svg viewBox="0 0 820 520" role="img" className="block h-full w-full">
      <rect width="820" height="520" rx="14" fill={c.night} />
      <rect x="1" y="1" width="818" height="518" rx="13" fill="none" stroke={c.line} strokeWidth="2" />

      <rect x="0" y="0" width="150" height="520" rx="14" fill="#101722" />
      <rect x="149" y="0" width="1" height="520" fill={c.line} />
      <CosalphaSvgLogo x="22" y="34" inverse />
      <text x="22" y="58" fill={c.text} fontFamily="Inter, system-ui, sans-serif" fontSize="15" fontWeight="800">Memo workspace</text>
      <StatusChip x="22" y="84" label="CREATE" w={62} />

      <rect x="22" y="128" width="106" height="70" rx="8" fill={c.panel2} stroke={c.line} />
      <text x="34" y="150" fill={c.mutedLight} fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" letterSpacing="1.2">MEMO SETUP</text>
      <SvgTextLine x="34" y="166" w="74" h="6" fill={c.line} />
      <SvgTextLine x="34" y="181" w="52" h="6" fill={c.line} />

      {["Industry overview", "Business segment analysis", "Financial analysis"].map((label, index) => {
        const y = 242 + index * 42;
        const active = index === 1;
        return (
          <g key={label}>
            <rect x="14" y={y} width="122" height="36" rx="6" fill={active ? c.accentSoft : "transparent"} stroke={active ? c.accent : c.softLine} />
            <text x="28" y={y + 22} fill={active ? c.text : c.mutedLight} fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700">
              {label}
            </text>
          </g>
        );
      })}

      <rect x="150" y="0" width="670" height="48" fill="#F7F7F4" />
      <rect x="172" y="14" width="205" height="22" rx="6" fill="#FFFFFF" stroke="#D9DEE7" />
      <text x="184" y="29" fill={c.night} fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700">Business segment analysis</text>
      <rect x="682" y="12" width="92" height="26" rx="7" fill={c.accent} />
      <text x="728" y="29" textAnchor="middle" fill="#FFFFFF" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800">Send for review</text>

      <rect x="150" y="48" width="670" height="472" fill="#F3F5F7" />
      <rect x="172" y="94" width="515" height="290" rx="10" fill="#FFFFFF" stroke="#D9DEE7" />
      <text x="188" y="122" fill={c.night} fontFamily="JetBrains Mono, ui-monospace, monospace" fontSize="12" fontWeight="800">**Quantitative Segment Performance**</text>
      <SvgTextLine x="188" y="152" w="450" h="7" fill="#111827" />
      <SvgTextLine x="188" y="172" w="475" h="7" fill="#111827" />
      <SvgTextLine x="188" y="192" w="370" h="7" fill="#111827" />

      <text x="188" y="238" fill={c.night} fontFamily="JetBrains Mono, ui-monospace, monospace" fontSize="12" fontWeight="800">**Subsidiary and Investment Activity**</text>
      <SvgTextLine x="188" y="268" w="458" h="7" fill="#111827" />
      <SvgTextLine x="188" y="288" w="430" h="7" fill="#111827" />
      <rect x="188" y="306" width="196" height="14" rx="3" fill="#FFE0C9" />
      <SvgTextLine x="194" y="310" w="184" h="6" fill="#111827" />
      <SvgTextLine x="188" y="332" w="410" h="7" fill="#111827" />

      <rect x="172" y="396" width="515" height="76" rx="10" fill="#FFFFFF" stroke="#D9DEE7" />
      <text x="188" y="422" fill={c.night} fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800">Ask about this section</text>
      <rect x="188" y="438" width="392" height="22" rx="6" fill="#F8FAFC" stroke="#D9DEE7" />
      <rect x="592" y="438" width="78" height="22" rx="7" fill={c.accent} />
      <text x="631" y="453" textAnchor="middle" fill="#FFFFFF" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800">Apply</text>

      <g transform="translate(704 96)">
        <text x="0" y="0" fill={c.night} fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800">Sources</text>
        {["Annual report p.190", "P&L model Q3", "Annual report p.67", "Debt schedule"].map((source, index) => (
          <g key={source} transform={`translate(0 ${28 + index * 30})`}>
            <circle cx="4" cy="4" r="4" fill={index === 1 ? c.accent : "rgba(255,255,255,0.35)"} />
            <text x="16" y="8" fill={c.night} fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="650">{source}</text>
          </g>
        ))}
        <rect x="0" y="174" width="92" height="50" rx="8" fill="#FFFFFF" stroke="#D9DEE7" />
        <text x="12" y="196" fill={c.night} fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800">Review</text>
        <StatusChip x="12" y="206" label="DRAFT" color={c.blue} w={50} />
      </g>

      <rect x="420" y="300" width="2" height="18" fill={c.accent}>
        <animate attributeName="opacity" values="1;0.25;1" dur="1.3s" repeatCount="indefinite" />
      </rect>
      <circle cx="392" cy="314" r="4" fill={c.accent}>
        <animate attributeName="r" values="3;7;3" dur="1.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="1;0.25;1" dur="1.6s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

function FinancialHighlightsSvg() {
  return <FinancialAnalysisVisual />;
}

function UseCasesProductVisual() {
  return (
    <ProductVisual
      src={PRODUCT_ASSETS.useCases}
      alt="Cosalpha report view with source-linked metrics and review workflow"
      fallback={FinancialHighlightsSvg}
      className="h-full min-h-0 w-full"
      frameLabel="Use cases product screenshot"
    />
  );
}

function ProductOutputVisual() {
  return (
    <ProductVisual
      src={PRODUCT_ASSETS.productOutput}
      alt="Cosalpha memo workspace with desk formatting and source panel"
      fallback={() => (
        <div className="aspect-[1000/860] overflow-hidden bg-white">
          <WorkspaceScreenSvg />
        </div>
      )}
      className="aspect-[1000/860] w-full"
      frameLabel="Product output screenshot"
    />
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[var(--cream)] text-[var(--ink)]" data-testid="landing-root">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-[var(--saffron)]/45 bg-[var(--night)] shadow-[0_14px_40px_rgba(11,15,20,0.28)]">
        <nav className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-4 text-white sm:px-8 lg:px-12">
          <Link to="/" aria-label="Cosalpha home" data-testid="nav-logo">
            <Logo size={25} inverse flourish />
          </Link>
          <div className="hidden items-center gap-8 md:flex">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="text-[12px] font-medium tracking-[0.08em] text-white/62 transition-colors hover:text-white"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {item.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/signup"
              className="marketing-cta-primary inline-flex items-center gap-1.5 border px-4 py-2 text-[13px] font-semibold transition-colors"
              data-testid="nav-signup"
            >
              Request early access <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.8} />
            </Link>
          </div>
        </nav>
      </header>

      <section className="relative overflow-hidden bg-[var(--night)] text-white">
        <div className="absolute inset-0 opacity-[0.18] [background-image:linear-gradient(rgba(255,255,255,.12)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.12)_1px,transparent_1px)] [background-size:56px_56px]" />
        <div className="relative mx-auto grid min-h-[92svh] max-w-[1440px] items-center gap-12 px-5 pb-12 pt-28 sm:px-8 lg:grid-cols-12 lg:px-12 lg:pb-16 lg:pt-32">
          <div className="lg:col-span-7">
            <SmallCaps className="text-[var(--saffron)]">Financial data context · documentation across finance</SmallCaps>
            <h1 className="mt-7 max-w-[920px] font-serif text-[46px] font-medium leading-[0.98] tracking-[-0.018em] text-white sm:text-[68px] lg:text-[88px]" data-testid="hero-heading">
              AI documentation infrastructure for credit, private markets, and investment desks.
            </h1>
            <p className="mt-7 max-w-[760px] text-[17px] leading-[1.7] text-white/76 sm:text-[19px]" data-testid="hero-subheading">
              Cosalpha helps analysts turn financial data, source documents, filings, MIS packs, and models into grounded memos, board packs, portfolio reviews, and investment documents.
            </p>
            <p className="mt-5 max-w-[720px] border-l border-[var(--saffron)]/70 pl-5 text-[15px] leading-[1.65] text-white/68">
              Every number keeps its context. Every claim stays reviewable. Every output follows your desk's format.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <ButtonLink to="/signup" testId="hero-cta-primary">
                Request early access <ArrowUpRight className="h-4 w-4" />
              </ButtonLink>
              <ButtonLink href="#workflow" variant="secondary" testId="hero-cta-secondary">
                See how it works <ArrowRight className="h-4 w-4" />
              </ButtonLink>
            </div>
          </div>
          <div className="flex items-center self-center lg:col-span-5">
            <HeroContextGraphPanel />
          </div>
        </div>
      </section>

      <section id="focus-teams" className="border-b border-[var(--border)] bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-14 sm:px-8 lg:px-12 lg:py-20">
          <SectionHeading
            label="Focus teams"
            title="Built for the desks that produce the documents."
            body="Cosalpha maps to how work actually runs — credit committees, fund operations, research and IC, transaction desks, and family-office reporting — not just institution type."
          />
          <div className="mt-10 grid gap-px bg-[var(--border)] sm:grid-cols-2 lg:grid-cols-5">
            {focusDeskTeams.map((desk) => (
              <div key={desk} className="bg-[var(--cream)] p-5 text-[14px] font-medium leading-snug text-[var(--ink)]/88">
                {desk}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="workflow" className="border-b border-[var(--border)] bg-[var(--cream)]">
        <div className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
          <SectionHeading
            label="Workflow"
            title="Ingest, structure, generate, review."
            body="Cosalpha is built around the finance documentation lifecycle: context first, document second, review always."
          />
          <div className="mt-10 grid gap-px bg-[var(--border)] lg:grid-cols-2">
            {coreThemes.map((theme) => (
              <article key={theme.title} className="bg-white p-7">
                <SmallCaps className="text-[var(--saffron)]">Core theme</SmallCaps>
                <h3 className="mt-5 font-serif text-[28px] font-medium tracking-[-0.012em]">{theme.title}</h3>
                <p className="mt-4 text-[14px] leading-[1.75] text-[var(--warm-grey)]">{theme.body}</p>
              </article>
            ))}
          </div>
          <div className="mt-12 grid gap-px bg-[var(--border)] lg:grid-cols-4">
            {workflow.map(({ icon: Icon, title, body }) => (
              <article key={title} className="bg-white p-7">
                <Icon className="h-5 w-5 text-[var(--saffron)]" strokeWidth={2} />
                <h3 className="mt-8 font-serif text-[25px] font-medium tracking-[-0.012em]">{title}</h3>
                <p className="mt-4 text-[14px] leading-[1.7] text-[var(--warm-grey)]">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        id="use-cases"
        className="scroll-mt-[4.5rem] border-b border-[var(--border)] bg-white lg:flex lg:min-h-[calc(100svh-4.5rem)] lg:max-h-[calc(100svh-4.5rem)]"
      >
        <div className="mx-auto flex w-full max-w-[1440px] flex-1 flex-col overflow-hidden px-5 py-12 sm:px-8 lg:px-12 lg:py-10">
          <div className="grid min-h-0 flex-1 gap-8 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] lg:items-stretch lg:gap-10">
            <div className="flex min-h-0 flex-col">
              <SectionHeading
                compact
                label="Use cases"
                title="Documentation across the finance domain."
                body="Built for desks that translate financial data context into reviewed documents — credit and underwriting, portfolio and fund ops, research and IC, transaction teams, and family-office reporting — across banks, NBFCs, private credit, PE/VC, and family offices."
              />
              <div className="mt-5 flex min-h-[220px] flex-1 items-center justify-center overflow-hidden sm:min-h-[260px] lg:mt-6 lg:min-h-0">
                <div className="h-full max-h-full min-h-0 w-full max-w-[520px]">
                  <UseCasesProductVisual />
                </div>
              </div>
            </div>
            <div className="flex min-h-[320px] flex-1 flex-col lg:min-h-0">
              <div className="flex h-full min-h-0 flex-1 flex-col gap-3">
                {useCases.map((useCase) => (
                  <article
                    key={useCase.title}
                    className="flex min-h-0 flex-1 flex-col border border-[var(--border)] bg-white p-5 transition-colors hover:border-[var(--saffron)]/50 sm:p-6"
                  >
                    <h3 className="font-serif text-[22px] font-medium leading-[1.08] tracking-[-0.01em] sm:text-[24px]">{useCase.title}</h3>
                    <p className="mt-2 flex-1 text-[13px] leading-[1.55] text-[var(--warm-grey)] sm:text-[14px] sm:leading-[1.6] lg:line-clamp-4">
                      {useCase.body}
                    </p>
                    <div className="mt-3 flex shrink-0 flex-wrap gap-2">
                      {useCase.tags.map((tag) => (
                        <span key={tag} className="border border-[var(--border)] bg-[var(--cream)] px-2.5 py-1 text-[11px] font-medium text-[var(--ink)]/72">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="trust" className="border-b border-white/10 bg-[var(--night)] text-white">
        <div className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
          <SectionHeading
            label="Trust layer"
            title="Built for teams that need a defensible record — of every source, every change, and every decision."
            body="Designed for teams that need a defensible record of which data was used, where it came from, what changed, and who reviewed it."
            dark
          />
          <p className="mt-8 max-w-[640px] text-[13px] italic leading-[1.65] text-white/48">
            In early discussions with NBFIs and venture debt teams.
          </p>
          <div className="mt-10 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {trustLayer.map(({ icon: Icon, title, body }) => (
              <article key={title} className="bg-[var(--night)] p-7">
                <Icon className="h-5 w-5 text-[var(--saffron)]" strokeWidth={1.9} />
                <h3 className="mt-7 font-serif text-[23px] font-medium tracking-[-0.01em] text-white">{title}</h3>
                <p className="mt-3 text-[13px] leading-[1.7] text-white/62">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="product-output" className="border-b border-[var(--border)] bg-white">
        <div className="mx-auto grid max-w-[1440px] gap-10 px-5 py-20 sm:px-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(420px,0.95fr)] lg:items-center lg:gap-16 lg:px-12 lg:py-28">
          <div>
            <ProductOutputVisual />
          </div>
          <div className="max-w-[620px]">
            <SectionHeading
              label="Product output"
              title="Reports that read like they came from your desk."
              body="Cosalpha studies document structure, section order, terminology, metric conventions, data context, and review patterns. The output is a draft your team can inspect, edit, and defend."
            />
            <div className="mt-10 grid gap-4">
              {productOutputBullets.map((item) => (
                <div key={item} className="flex min-h-[64px] items-center gap-4 border border-[var(--border)] bg-[var(--cream)] px-6 py-4 text-[15px] leading-[1.45] text-[var(--ink)]/82">
                  <Check className="h-4 w-4 shrink-0 text-[var(--saffron)]" strokeWidth={2.2} />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="early-access" className="bg-[var(--night)] text-white">
        <div className="mx-auto grid max-w-[1440px] gap-10 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:px-12 lg:py-24">
          <div className="lg:col-span-7">
            <SmallCaps className="text-[var(--saffron)]">Early access</SmallCaps>
            <h2 className="mt-5 font-serif text-[36px] font-medium leading-[1.05] tracking-[-0.015em] sm:text-[50px]">
              See Cosalpha on a workflow your desk actually runs.
            </h2>
          </div>
          <div className="self-end lg:col-span-5">
            <p className="text-[15px] leading-[1.75] text-white/68">
              Bring a workflow your desk runs today. We'll show you how Cosalpha handles it.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <ButtonLink to="/signup" testId="demo-cta-primary">
                Request early access <ArrowUpRight className="h-4 w-4" />
              </ButtonLink>
              <a
                href="mailto:hello@cosalpha.ai"
                className="inline-flex items-center gap-2 border border-white/22 px-6 py-3 text-[14px] font-semibold text-white transition-colors hover:border-white/45 hover:bg-white/10"
                data-testid="demo-cta-email"
              >
                Email specific requests <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-[var(--night)] text-white">
        <div className="mx-auto max-w-[1440px] px-5 py-14 sm:px-8 lg:px-12">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <Logo size={34} inverse flourish />
              <p className="mt-6 max-w-[480px] text-[13px] leading-[1.7] text-white/62">
                Cosalpha builds AI documentation infrastructure for finance teams that need financial data context, source-grounded memos, research notes, portfolio reviews, and investment documents.
              </p>
            </div>
            <div className="grid gap-8 text-[12px] sm:grid-cols-3 md:col-span-7">
              {[
                {
                  title: "Platform",
                  links: [
                    { label: "Workflow", href: "#workflow" },
                    { label: "Use cases", href: "#use-cases" },
                    { label: "Trust layer", href: "#trust" },
                  ],
                },
                {
                  title: "Access",
                  links: [
                    { label: "Request early access", to: "/signup" },
                    { label: "Contact", href: "mailto:hello@cosalpha.ai" },
                  ],
                },
                {
                  title: "Domains",
                  links: [
                    { label: "Banks", href: "#use-cases" },
                    { label: "NBFCs", href: "#use-cases" },
                    { label: "Private credit", href: "#use-cases" },
                  ],
                },
              ].map((group) => (
                <div key={group.title}>
                  <SmallCaps className="text-[var(--saffron)]">{group.title}</SmallCaps>
                  <ul className="mt-4 space-y-2.5">
                    {group.links.map((link) => (
                      <li key={link.label}>
                        <FooterLink {...link} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-12 flex flex-col gap-3 border-t border-white/10 pt-6 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/42 sm:flex-row sm:items-center sm:justify-between">
            <span>© {new Date().getFullYear()} Cosalpha · Sydney · Mumbai</span>
            <span>Financial data context · finance documentation</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
